import React, { FC, memo, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { IEventDetailsStoreState } from '../../../../store/event/eventDetails/EventDetailsStoreState';
import { EventHelper } from '../../../../helpers';
import { Button, PageFooter, TextInput } from '../../../../components/UI';
import { Routes } from '../../../../store/routes';
import { IEventStoreState } from '../../../../store/event/types';
import { useRoutesHelper } from '../../../../helpers/routesHelper';
import { useStartDistributeV1Mutation } from '../../../../core/api/eventsSlice';
import { useCreateDistributorByEmailMutation } from '../../../../core/api/accountSlice';
import { GlobalStateType } from '../../../../configureStore';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

type EventDetailsFooterType = {
  event: IEventDetailsStoreState;
  isInitializing: boolean;
};

export const EventDetailsFooterV1: FC<EventDetailsFooterType> = memo(({ event, isInitializing }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { getBookRoute, createReferralLink } = useRoutesHelper();

  const isAuthorized = useSelector<GlobalStateType, boolean>((state) => state.app.isAuthorized);
  const userEvents = useSelector<GlobalStateType, Array<IEventStoreState>>((state) => state.userEventsV1);

  const [startDistributeRequest, { isLoading: isLoadingStartDistribute }] = useStartDistributeV1Mutation();
  const [createDistributor, { isLoading: isLoadingCreateDistributor }] = useCreateDistributorByEmailMutation();

  const [showGetLink, setShowGetLink] = useState(false);
  const [email, setEmail] = useState('');
  const [referralSlug, setReferralSlug] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  const referralUrl = useMemo(() => (referralSlug ? createReferralLink(referralSlug) : ''), [createReferralLink, referralSlug]);
  const hasLink = !!referralUrl;
  const canCreate = isValidEmail(email) && !isLoadingCreateDistributor && !hasLink;

  const copyToClipboard = async (text: string) => {
    if (navigator?.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch { return false; }
  };

  const handleGetLink = async () => {
    if (hasLink) {
      const ok = await copyToClipboard(referralUrl);
      if (ok) { setIsCopied(true); window.setTimeout(() => setIsCopied(false), 5000); }
      return;
    }
    if (!canCreate) return;
    try {
      const res = await createDistributor({ email: email.trim() }).unwrap();
      if (res?.referralSlug) setReferralSlug(res.referralSlug);
    } catch { /* user can retry */ }
  };

  if (!event) {
    return null;
  }
  const isDomain = isEmpty(event.domains);

  const isDistributed = EventHelper.isEventDetailsDistributed(event, userEvents);
  const sellTicketButtonTitle = isDistributed ? t('Event.SellTicket') : t('Event.BecomeDistributor');
  const showDistributionButton = isAuthorized && !isInitializing && event.isDistributionAvailable;
  const isBookingEnabled = !event.notAvailableForPurchase;

  const getBookTicketsButtonTitle = () => {
    let bookTicketsButtonTitle = t('Event.BuyTicket');

    if (!isBookingEnabled) {
      bookTicketsButtonTitle = t(event.notAvailableForPurchaseReason);
    }
    if (isDomain) {
      bookTicketsButtonTitle = t('Event.TemporarilyUnavailable');
    }

    return bookTicketsButtonTitle;
  };

  const bookTicketsButtonTitle = getBookTicketsButtonTitle();

  const handleOnBookClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }
    navigate(getBookRoute(event.slug, null));
  };

  const handleOnDistributeClick = (e: MouseEvent<HTMLButtonElement>, event: IEventDetailsStoreState) => {
    if (e) {
      e.preventDefault();
    }

    if (EventHelper.isEventDetailsDistributed(event, userEvents)) {
      navigate(getBookRoute(event.slug, null, null, null, null, true));
      return;
    }

    (async () => await startDistributeRequest(event.slug))().then(() => {
      navigate(Routes.UserEventsV1);
    });
  };

  return (
    <PageFooter>
      <Button disabled={!isBookingEnabled || isDomain} onClick={(e) => handleOnBookClick(e)}>
        {bookTicketsButtonTitle}
      </Button>
      {showGetLink ? (
        <GetLinkForm>
          {hasLink ? (
            <GetLinkDisplayUrl>{referralUrl}</GetLinkDisplayUrl>
          ) : (
            <GetLinkInput
              type="email"
              value={email}
              placeholder="you@example.com"
              inputMode="email"
              autoComplete="email"
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleGetLink(); }}
            />
          )}
          <Button
            disabled={hasLink ? false : !canCreate}
            isLoading={isLoadingCreateDistributor}
            onClick={() => void handleGetLink()}
          >
            {isLoadingCreateDistributor ? 'Please wait...' : hasLink ? (isCopied ? t('Share.Copied') : t('Share.CopyLink')) : t('Dashboard.EarnOnATicket')}
          </Button>
        </GetLinkForm>
      ) : (
        <Button outline onClick={() => setShowGetLink(true)}>
          {t('Dashboard.EarnOnATicket')}
        </Button>
      )}
      {showDistributionButton && (
        <Button
          disabled={isDomain || isLoadingStartDistribute}
          isLoading={isLoadingStartDistribute}
          onClick={(e) => handleOnDistributeClick(e, event)}
        >
          {sellTicketButtonTitle}
        </Button>
      )}
    </PageFooter>
  );
});

const GetLinkForm = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GetLinkInput = styled(TextInput as any)`
  padding: 15px 20px;
  font-size: 13px;
`;

const GetLinkDisplayUrl = styled.div`
  width: 100%;
  padding: 15px 20px;
  border: 1px solid ${(props) => props.theme.colors.accent};
  border-radius: 24px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.accent};
  word-break: break-all;
  text-align: center;
  user-select: all;
`;
